import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import {
    fetchContacts,
    fetchSingleContact,
    addContact,
    updateContact,
    deleteContact,
    setPrimaryContact,
    updateContactOrder,
    DeleteContact
} from './contactAsyncThunk';
import { Contact } from '../../../types';
import { toast } from 'react-toastify';

interface ContactState {
    data: Contact[];
    selectedContact: Contact | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    loading: boolean;
    error: string | null;
}

const initialState: ContactState = {
    data: [],
    selectedContact: null,
    status: 'idle',
    loading: false,
    error: null,
};

const contactSlice = createSlice({
    name: 'contact',
    initialState,
    reducers: {
        setSelectedContact: (state, action: PayloadAction<Contact | null>) => {
            state.selectedContact = action.payload;
        },
        clearContacts: (state) => {
            state.data = [];
            state.selectedContact = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch all
        builder
            .addCase(fetchContacts.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchContacts.fulfilled, (state, action: PayloadAction<Contact[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchContacts.rejected, (state, action) => {
                state.status = 'failed';
                toast.error(action.payload || 'Failed to fetch contacts');
                state.error = action.payload || 'Failed to fetch contacts';
            });

        // Fetch single
        builder
            .addCase(fetchSingleContact.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSingleContact.fulfilled, (state, action: PayloadAction<Contact>) => {
                state.loading = false;
                state.selectedContact = action.payload;
            })
            .addCase(fetchSingleContact.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to fetch contact');
                state.error = action.payload || 'Failed to fetch contact';
            });

        // Add
        builder
            .addCase(addContact.pending, (state) => {
                state.loading = true;
            })
            .addCase(addContact.fulfilled, (state, action: PayloadAction<Contact>) => {
                state.loading = false;
                state.data.push(action.payload);
                toast.success('Contact added successfully');
            })
            .addCase(addContact.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to add contact');
                state.error = action.payload || 'Failed to add contact';
            });

        // Update
        builder
            .addCase(updateContact.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateContact.fulfilled, (state, action: PayloadAction<Contact>) => {
                const index = state.data.findIndex((item) => item.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
                if (state.selectedContact?.id === action.payload.id) {
                    state.selectedContact = action.payload;
                }
                state.loading = false;
                toast.success('Contact updated successfully');
            })
            .addCase(updateContact.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to update contact');
                state.error = action.payload || 'Failed to update contact';
            });

        // Delete
        builder
            .addCase(deleteContact.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteContact.fulfilled, (state, action: PayloadAction<DeleteContact>) => {
                state.data = state.data.filter((item) => item.id !== action.payload.id);
                if (state.selectedContact?.id === action.payload.id) {
                    state.selectedContact = null;
                }
                state.loading = false;
                toast.success('Contact deleted successfully');
            })
            .addCase(deleteContact.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to delete contact');
                state.error = action.payload || 'Failed to delete contact';
            });

        // Set primary
        builder
            .addCase(setPrimaryContact.pending, (state) => {
                state.loading = true;
            })
            .addCase(setPrimaryContact.fulfilled, (state, action: PayloadAction<Contact>) => {
                // Update all contacts to not be primary
                state.data = state.data.map(contact => ({
                    ...contact,
                    isPrimary: contact.id === action.payload.id
                }));
                state.loading = false;
                toast.success('Primary contact set successfully');
            })
            .addCase(setPrimaryContact.rejected, (state, action) => {
                state.loading = false;
                toast.error(action.payload || 'Failed to set primary contact');
                state.error = action.payload || 'Failed to set primary contact';
            });

        // Update order
        builder
            .addCase(updateContactOrder.pending, (state) => {
                state.status = 'loading';
                state.loading = true;
            })
            .addCase(updateContactOrder.fulfilled, (state, action: PayloadAction<Contact[]>) => {
                state.data = action.payload;
                state.status = 'succeeded';
                state.loading = false;
                toast.success('Order updated successfully');
            })
            .addCase(updateContactOrder.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                toast.error(action.payload || 'Failed to update order');
                state.error = action.payload || 'Failed to update order';
            });
    },
});

export const { setSelectedContact, clearContacts } = contactSlice.actions;

export const selectContact = (state: RootState) => state.contact;

export default contactSlice.reducer;
